import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Info, Save, Send, X, ChevronRight, CheckCircle2, Printer, Download } from "lucide-react";
import { jsPDF } from "jspdf";
import useOnlineEnrollment from "@/hooks/useOnlineEnrollment";
import StudentEnrollmentDocuments from "@/pages/student/enrollment/StudentEnrollmentDocuments";
import { EnrollmentStatus, EnrollmentStatusTimeline, getStatusMeta } from "@/utils/enrolmentStatus";
import { studentDocumentsService } from "@/services/studentDocumentsService";
import { studentEnrollmentWorkflowService } from "@/services/studentEnrollmentWorkflowService";
import EnrollmentWorkflowTimeline from "@/components/EnrollmentWorkflowTimeline";
import ToastHelper from "@/utils/toastHelper";
const REQ = <span className="text-rose-600 ml-0.5">*</span>;

const SECTIONS = [
  { id: "usi", title: "Unique Student Identifier" },
  { id: "personal", title: "Personal details" },
  { id: "language", title: "Language and cultural diversity" },
  { id: "schooling", title: "Schooling" },
  { id: "education", title: "Education" },
  { id: "employment", title: "Employment" },
  { id: "disability", title: "Disability" },
  { id: "study", title: "Study reason" },
  { id: "lln", title: "Language, Literacy and Numeracy" },
  { id: "documents", title: "My Documents" },
  { id: "declaration", title: "Declaration" },
];

const cbToggle = (arr, v) => arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

const uniqueById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const id = String(item?.id ?? "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export default function OnlineEnrolmentNew() {
  const location = useLocation();
  const studentBookingId = location.state?.studentBookingId || null;

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    const frameId = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, [studentBookingId]);

  const {
    d,
    set,
    active,
    setActive,
    submission,
    loading,
    loadError,
    masterData,
    masterLoading,
    saving,
    requiredCount,
    canSubmit,
    workFlowStatusNo,
    documents,
    setDocuments,
    save,
    saveAndReturn,
    submit,
    cancel,
    jumpTo,
  } = useOnlineEnrollment(studentBookingId);
  const [showDraftWorkflowHistory, setShowDraftWorkflowHistory] = useState(false);

  useEffect(() => {
    if (typeof window.IntersectionObserver !== "function") return undefined;

    const sections = SECTIONS
      .map((section) => document.getElementById(`sec-${section.id}`))
      .filter(Boolean);

    if (sections.length === 0) return undefined;

    const observer = new window.IntersectionObserver((entries) => {
      const visibleSection = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (visibleSection) {
        setActive(visibleSection.target.id.replace(/^sec-/, ""));
      }
    }, {
      rootMargin: "-18% 0px -58% 0px",
      threshold: [0.05, 0.2, 0.4, 0.6],
    });

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [loading, setActive]);

  // Normalize titles so the select can render regardless of backend shape
  const titlesList = (masterData?.titles || []).map((t, idx) => {
    if (!t && typeof t !== "string") return { id: `t-${idx}`, name: String(t) };
    if (typeof t === "string") return { id: t, name: t };
    return { id: t.id ?? t.titleId ?? t.value ?? `t-${idx}`, name: t.name ?? t.title ?? t.text ?? t.label ?? String(t) };
  });

  // Normalize genders similarly so the select renders whatever the API sends
  const gendersList = (masterData?.genders || []).map((g, idx) => {
    if (!g && typeof g !== "string") return { id: `g-${idx}`, name: String(g) };
    if (typeof g === "string") return { id: g, name: g };
    return { id: g.id ?? g.genderId ?? g.value ?? `g-${idx}`, name: g.name ?? g.text ?? g.label ?? g.gender ?? String(g) };
  });

  const statesList = (masterData?.states || []).map((s, idx) => ({ id: s.id ?? `s-${idx}`, name: s.name ?? String(s) }));
  const citiesList = (masterData?.cities || []).map((c, idx) => ({ id: c.id ?? `city-${idx}`, name: c.name ?? String(c) }));
  const addressCountriesList = (masterData?.countries || []).map((c, idx) => ({ id: c.id ?? `country-${idx}`, name: c.name ?? String(c) }));
  const postalStatesList = (masterData?.postalStates || masterData?.states || []).map((s, idx) => ({ id: s.id ?? `ps-${idx}`, name: s.name ?? String(s) }));
  const postalCitiesList = (masterData?.postalCities || masterData?.cities || []).map((c, idx) => ({ id: c.id ?? `pcity-${idx}`, name: c.name ?? String(c) }));
  const countriesList = (masterData?.countriesOfBirth || masterData?.countries || []).map((c, idx) => ({ id: c.id ?? `c-${idx}`, name: c.name ?? String(c) }));
  const postalCountriesList = (masterData?.postalCountries || masterData?.countries || []).map((c, idx) => ({ id: c.id ?? `pc-${idx}`, name: c.name ?? String(c) }));
  const indigenousList = (masterData?.indigenousStatus || []).map((i, idx) => ({ id: i.id ?? `i-${idx}`, name: i.name ?? String(i) }));
  const englishList = (masterData?.englishProficiency || []).map((e, idx) => ({ id: e.id ?? `e-${idx}`, name: e.name ?? String(e) }));
  const studyReasonsList = (masterData?.studyReasons || []).map((r, idx) => ({ id: r.id ?? `r-${idx}`, name: r.name ?? String(r) }));
  const highestSchoolList = (masterData?.highestSchoolLevels || []).map((h, idx) => ({ id: h.id ?? `h-${idx}`, name: h.name ?? String(h) }));
  const yesNoList = (masterData?.yesNo || []).map((option, index) => {
    const name = option?.name ?? String(option ?? "");
    return { id: name || `yn-${index}`, name };
  });
  const yesNoOptions = yesNoList.length > 0 ? yesNoList : [{ id: "Yes", name: "Yes" }, { id: "No", name: "No" }];
  const schoolYearsList = (masterData?.schoolCompletedYears || []).map((y, idx) => ({ id: y.id ?? `y-${idx}`, name: y.name ?? String(y) }));
  const emergencyRelationshipsList = (masterData?.emergencyRelationships || []).map((relationship, idx) => ({
    id: relationship.id ?? `relationship-${idx}`,
    name: relationship.name ?? String(relationship),
  }));
  const highestQualificationsList = uniqueById(masterData?.highestQualifications);
  const disabilityTypesList = uniqueById(masterData?.disabilityTypes);

  if (submission) return <SubmissionView submission={submission} masterData={masterData} documents={documents} setDocuments={setDocuments} studentEnrollmentId={d.studentEnrollmentId} />;

  if (!studentBookingId && !d.studentBookingId) {
    return (
      <div className="mx-auto max-w-2xl py-12" data-testid="enrolment-booking-required">
        <div className="wc-card p-8 text-center">
          <Info className="mx-auto h-10 w-10 text-orange-500" />
          <h1 className="mt-4 text-2xl font-display font-extrabold text-[#0A2540]">Select a course booking first</h1>
          <p className="mt-2 text-sm text-slate-600">
            This enrolment form must be opened from one of your course bookings. Please go to the Courses tab and select the course you want to enrol in.
          </p>
          <Link to="/student/my-courses" className="wc-btn-primary mt-6 inline-flex justify-center" data-testid="go-to-courses">
            Go to Courses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="online-enrolment-new">
      {studentBookingId && loading ? (
        <div className="wc-card p-6 text-center text-slate-600">Loading enrolment detailsâ€¦</div>
      ) : null}
      {studentBookingId && !loading && loadError ? (
        <div className="wc-card p-6 text-center text-slate-600">No enrolment data found for this booking ID.</div>
      ) : null}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-orange-600 font-semibold">Refresher FSS Hospitality</div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540] mt-1">Complete your enrolment form</h1>
          <p className="text-sm text-slate-500 mt-1">Fields marked with <span className="text-rose-600 font-bold">*</span> are required. {requiredCount.done} of {requiredCount.total} required fields completed ({requiredCount.percentage}%).</p>
          <div className="mt-2 h-2 max-w-md overflow-hidden rounded-full bg-slate-200" aria-label={`Enrolment progress ${requiredCount.percentage}%`}>
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${requiredCount.percentage}%` }} />
          </div>
          <StatusProgress status={workFlowStatusNo} />
        </div>
        <div className="flex flex-wrap gap-2"><>{d.studentEnrollmentId > 0 && <button type="button" className="wc-btn-outline text-sm" onClick={() => setShowDraftWorkflowHistory((value) => !value)}>{showDraftWorkflowHistory ? "Hide history" : "View workflow history"}</button>}</><button type="button" onClick={save} disabled={saving} className="wc-btn-outline text-sm shrink-0 inline-flex items-center gap-1.5" data-testid="fn-save-top">
          <Save className="w-4 h-4" /> {saving ? "Savingâ€¦" : "Save progress"}
        </button></div>
      </div>

      {showDraftWorkflowHistory && d.studentEnrollmentId > 0 && <div className="wc-card p-6"><h2 className="mb-4 text-lg font-display font-extrabold text-[#0A2540]">Workflow history</h2><EnrollmentWorkflowTimeline studentEnrollmentId={d.studentEnrollmentId} loadHistory={studentEnrollmentWorkflowService.getWorkflowHistory} /></div>}

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Section navigator */}
        <aside className="h-fit lg:sticky lg:top-24">
          <div className="wc-card max-h-[calc(100vh-7rem)] overflow-y-auto p-3">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 px-2 pb-2">Jump to</div>
            {SECTIONS.map((s) => (
              <button key={s.id} onClick={() => jumpTo(s.id)} type="button"
                className={`w-full text-left flex items-center justify-between text-sm px-2 py-2 rounded-md transition-colors ${active === s.id ? "bg-orange-50 text-[#EA580C] font-semibold" : "text-slate-700 hover:bg-slate-50"}`}
                data-testid={`jump-${s.id}`}>
                <span>{s.title}</span>
                <ChevronRight className="w-3.5 h-3.5 opacity-40" />
              </button>
            ))}
          </div>
        </aside>

        <form onSubmit={submit} className="space-y-6">
          {/* USI */}
          <Section id="usi" title="Unique Student Identifier" onFocus={() => setActive("usi")}>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900 mb-4 flex gap-3">
              <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <p>To complete any Accredited Training, government regulations require students to have a Unique Student Identifier (USI). If you do not have one, please create one now by <a href="https://www.usi.gov.au" target="_blank" rel="noreferrer" className="underline font-semibold">clicking here</a> and then enter the 10-digit code below. Government regulations prohibit us from issuing a statement of attainment without a valid USI.</p>
            </div>
            <F label={<>Unique Student Identifier {REQ}</>}>
              <input required maxLength={10} className="wc-input max-w-sm" value={d.usiNumber} onChange={(e) => set("usiNumber", e.target.value.toUpperCase())} placeholder="10-digit USI" data-testid="fn-usi" />
            </F>
          </Section>

          {/* Personal */}
          <Section id="personal" title="Personal details" onFocus={() => setActive("personal")}>
            <Grid>
              <F label={<>First name {REQ}</>} hint="Spelling must match the info you supplied for your USI"><input required className="wc-input" value={d.firstName} onChange={(e) => set("firstName", e.target.value)} data-testid="fn-firstName" /></F>
              <F label="Middle name/s (if applicable)"><input className="wc-input" value={d.middleName} onChange={(e) => set("middleName", e.target.value)} data-testid="fn-middleName" /></F>
              <F label={<>Surname {REQ}</>}><input required className="wc-input" value={d.lastName} onChange={(e) => set("lastName", e.target.value)} data-testid="fn-lastName" /></F>
              <F label={<>Title {REQ}</>}>
                  <select required className="wc-input" value={d.titleId || d.title} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (titlesList.find(x => String(x.id) === String(val)) || { name: val });
                      set("titleId", val);
                      set("title", sel.name);
                    }} data-testid="fn-title">
                    <option value="">Chooseâ€¦</option>
                          {masterLoading ? (
                            <option disabled>Loadingâ€¦</option>
                          ) : (masterData?.titles && masterData.titles.length > 0) ? (
                            titlesList.map((t) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))
                          ) : (
                            <option disabled>No data found</option>
                          )}
                  </select>
              </F>
              <F label={<>Date of birth {REQ}</>}><input required type="date" className="wc-input" value={d.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} data-testid="fn-dateOfBirth" /></F>
              <F label={<>Gender {REQ}</>}>
                <select required className="wc-input" value={d.genderId || d.gender} onChange={(e) => {
                    const val = e.target.value;
                    // find label
                    const sel = (gendersList.find(x => String(x.id) === String(val)) || { name: val });
                    set("genderId", val);
                    set("gender", sel.name);
                  }} data-testid="fn-gender">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (
                    <option disabled>Loadingâ€¦</option>
                  ) : (masterData?.genders && masterData.genders.length > 0) ? (
                    gendersList.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F label="Home phone number" hint="Include state prefix (e.g. 03 for VIC, 02 for NSW)"><input className="wc-input" value={d.homePhone} onChange={(e) => set("homePhone", e.target.value)} data-testid="fn-homephone" /></F>
              <F label="Work phone number"><input className="wc-input" value={d.workPhone} onChange={(e) => set("workPhone", e.target.value)} data-testid="fn-workphone" /></F>
              <F label={<>Mobile phone number {REQ}</>}><input required className="wc-input" value={d.mobileNumber} onChange={(e) => set("mobileNumber", e.target.value)} data-testid="fn-mobileNumber" /></F>
              <F label={<>Email {REQ}</>}><input required type="email" className="wc-input" value={d.email} onChange={(e) => set("email", e.target.value)} data-testid="fn-email" /></F>
              <F label={<>Preferred contact type {REQ}</>}>
                <select required className="wc-input" value={d.preferredContactTypeId} onChange={(e) => set("preferredContactTypeId", e.target.value)} data-testid="fn-prefcontact">
                  <option value="">Choose…</option>
                  {(masterData.preferredContactTypes || []).map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </F>
            </Grid>

            <div className="text-xs uppercase font-semibold tracking-wide text-slate-500 mt-6 mb-2">Emergency contact</div>
            <Grid>
              <F label={<>Emergency contact name {REQ}</>}><input required className="wc-input" value={d.emergencyContactName} onChange={(e) => set("emergencyContactName", e.target.value)} data-testid="fn-emergency-name" /></F>
              <F label="Relationship">
                <select className="wc-input" value={d.emergencyContactRelationshipId || ""} onChange={(e) => {
                  const value = e.target.value;
                  const relationship = emergencyRelationshipsList.find((item) => String(item.id) === value);
                  set("emergencyContactRelationshipId", value);
                  set("emergencyRelationship", relationship?.name || "");
                }} data-testid="fn-emergency-relationship">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (<option disabled>Loadingâ€¦</option>) : emergencyRelationshipsList.length > 0 ? (emergencyRelationshipsList.map((relationship) => <option key={relationship.id} value={relationship.id}>{relationship.name}</option>)) : (<option disabled>No data found</option>)}
                </select>
              </F>
              <F label={<>Emergency contact phone {REQ}</>}><input required className="wc-input" value={d.emergencyPhone} onChange={(e) => set("emergencyPhone", e.target.value)} data-testid="fn-emergency-phone" /></F>
            </Grid>

            <div className="text-xs uppercase font-semibold tracking-wide text-slate-500 mt-6 mb-2">Home address</div>
            <Grid>
              <F label="Unit / flat / level (optional)" hint="e.g. Unit 5, Level 4 or Suite 21A"><input className="wc-input" autoComplete="address-line2" value={d.flatUnitDetails} onChange={(e) => set("flatUnitDetails", e.target.value)} data-testid="fn-flat-unit-details" /></F>
              <F label="Building / property name (optional)"><input className="wc-input" value={d.buildingPropertyName} onChange={(e) => set("buildingPropertyName", e.target.value)} data-testid="fn-buildingPropertyName" /></F>
              <F label={<>Street number {REQ}</>} hint="e.g. 205 or Lot 118"><input required className="wc-input" value={d.streetNumber} onChange={(e) => set("streetNumber", e.target.value)} data-testid="fn-streetno" /></F>
              <F label={<>Street name and type {REQ}</>} hint="e.g. Collins Street"><input required autoComplete="address-line1" className="wc-input" value={d.streetName} onChange={(e) => set("streetName", e.target.value)} data-testid="fn-streetname" /></F>
              <F label={<>Suburb / locality {REQ}</>}><input required autoComplete="address-level2" className="wc-input" value={d.suburb} onChange={(e) => set("suburb", e.target.value)} data-testid="fn-suburb" /></F>
              <F label={<>State / territory {REQ}</>}>
                <select required autoComplete="address-level1" className="wc-input" value={d.stateId || d.state} onChange={(e) => {
                    const val = e.target.value;
                    const sel = (statesList.find(x => String(x.id) === String(val)) || { name: val });
                    set("stateId", val);
                    set("state", sel.name);
                  }} data-testid="fn-state">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (
                    <option disabled>Loadingâ€¦</option>
                  ) : (statesList.length > 0) ? (
                    statesList.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F label={<>Postcode {REQ}</>} hint="Australian postcodes contain four digits"><input required autoComplete="postal-code" inputMode="numeric" pattern="[0-9]{4}" minLength={4} maxLength={4} className="wc-input" value={d.postCode} onChange={(e) => set("postCode", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="fn-postCode" /></F>
              <F label={<>Country {REQ}</>}>
                <select required autoComplete="country" className="wc-input" value={d.countryId || ""} onChange={(e) => set("countryId", e.target.value)} data-testid="fn-country">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (<option disabled>Loadingâ€¦</option>) : addressCountriesList.length > 0 ? (addressCountriesList.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)) : (<option disabled>No data found</option>)}
                </select>
              </F>
              <F label="City lookup (optional)">
                <select className="wc-input" value={d.cityId || ""} onChange={(e) => set("cityId", e.target.value)} data-testid="fn-city">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (<option disabled>Loadingâ€¦</option>) : citiesList.length > 0 ? (citiesList.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)) : (<option disabled>No data found</option>)}
                </select>
              </F>
            </Grid>

            <div className="text-xs uppercase font-semibold tracking-wide text-slate-500 mt-6 mb-2">Postal address</div>
            <F label={<>Is your postal address different to your current home address? {REQ}</>}>
              <div className="flex gap-4">
                {["Yes", "No"].map((o) => (
                  <label key={o} className="inline-flex items-center gap-2 text-sm">
                    <input type="radio" name="sameAsResidential" value={o} checked={d.sameAsResidential === (o === "No")} onChange={() => set("sameAsResidential", o === "No")} data-testid={`fn-postaldiff-${o.toLowerCase()}`} />{o}
                  </label>
                ))}
              </div>
            </F>
            {d.sameAsResidential === false && (
              <Grid className="mt-4">
                <F label="Postal unit / flat / level (optional)"><input autoComplete="address-line2" className="wc-input" value={d.postalFlatUnitDetails} onChange={(e) => set("postalFlatUnitDetails", e.target.value)} data-testid="fn-postal-flat-unit" /></F>
                <F label="Postal building / property name (optional)"><input className="wc-input" value={d.postalBuildingPropertyName} onChange={(e) => set("postalBuildingPropertyName", e.target.value)} data-testid="fn-postal-building" /></F>
                <F label={<>Postal street number {REQ}</>}><input required className="wc-input" value={d.postalStreetNumber} onChange={(e) => set("postalStreetNumber", e.target.value)} data-testid="fn-postal-street-number" /></F>
                <F label={<>Postal street name and type {REQ}</>}><input required autoComplete="address-line1" className="wc-input" value={d.postalStreetName} onChange={(e) => set("postalStreetName", e.target.value)} data-testid="fn-postal-street-name" /></F>
                <F label={<>Postal suburb / locality {REQ}</>}><input required autoComplete="address-level2" className="wc-input" value={d.postalSuburb} onChange={(e) => set("postalSuburb", e.target.value)} data-testid="fn-postalsuburb" /></F>
                <F label={<>Postal state / territory {REQ}</>}>
                  <select required autoComplete="address-level1" className="wc-input" value={d.postalStateId || d.postalState} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (postalStatesList.find(x => String(x.id) === String(val)) || { name: val });
                      set("postalStateId", val);
                      set("postalState", sel.name);
                    }} data-testid="fn-postalstate">
                    <option value="">Chooseâ€¦</option>
                    {masterLoading ? (<option disabled>Loadingâ€¦</option>) : (postalStatesList.length > 0) ? (postalStatesList.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))) : (<option disabled>No data found</option>)}
                  </select>
                </F>
                <F label={<>Postal postcode {REQ}</>}><input required autoComplete="postal-code" inputMode="numeric" pattern="[0-9]{4}" minLength={4} maxLength={4} className="wc-input" value={d.postalPostCode} onChange={(e) => set("postalPostCode", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="fn-postalpc" /></F>
                <F label={<>Postal country {REQ}</>}>
                  <select required autoComplete="country" className="wc-input" value={d.postalCountryId || ""} onChange={(e) => set("postalCountryId", e.target.value)} data-testid="fn-postalcountry">
                    <option value="">Chooseâ€¦</option>
                    {masterLoading ? (<option disabled>Loadingâ€¦</option>) : postalCountriesList.length > 0 ? (postalCountriesList.map((country) => <option key={country.id} value={country.id}>{country.name}</option>)) : (<option disabled>No data found</option>)}
                  </select>
                </F>
                <F label="Postal city lookup (optional)">
                  <select className="wc-input" value={d.postalCityId || ""} onChange={(e) => set("postalCityId", e.target.value)} data-testid="fn-postalcity">
                    <option value="">Chooseâ€¦</option>
                    {masterLoading ? (<option disabled>Loadingâ€¦</option>) : postalCitiesList.length > 0 ? (postalCitiesList.map((city) => <option key={city.id} value={city.id}>{city.name}</option>)) : (<option disabled>No data found</option>)}
                  </select>
                </F>
              </Grid>
            )}
          </Section>

          {/* Language & cultural */}
          <Section id="language" title="Language and cultural diversity" onFocus={() => setActive("language")}>
            <Grid>
              <F label={<>In what country were you born? {REQ}</>}>
                <select required className="wc-input" value={d.countryOfBirthId || d.countryOfBirth} onChange={(e) => {
                    const val = e.target.value;
                    const sel = (countriesList.find(x => String(x.id) === String(val)) || { name: val });
                    set("countryOfBirthId", val);
                    set("countryOfBirth", sel.name);
                  }} data-testid="fn-borncountry">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (<option disabled>Loadingâ€¦</option>) : (countriesList.length > 0) ? (countriesList.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))) : (<option disabled>No data found</option>)}
                </select>
              </F>
              <F label="In which town / city were you born?"><input className="wc-input" value={d.townOfBirth} onChange={(e) => set("townOfBirth", e.target.value)} data-testid="fn-borncity" /></F>
              <F label={<>Are you an Australian citizen? {REQ}</>} full>
                <div className="flex gap-4">
                  {["Yes", "No"].map((o) => <label key={o} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="aus" value={o} checked={d.australianCitizen === o} onChange={(e) => set("australianCitizen", e.target.value)} data-testid={`fn-aus-${o.toLowerCase()}`} />{o}</label>)}
                </div>
              </F>
              <F label={<>Are you of Aboriginal or Torres Strait Islander origin? {REQ}</>}>
                <select required className="wc-input" value={d.indigenousStatusId || d.indigenousStatus} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (indigenousList.find(x => String(x.id) === String(val)) || { name: val });
                      set("indigenousStatusId", val);
                      set("indigenousStatus", sel.name);
                    }} data-testid="fn-aboriginal">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (
                    <option disabled>Loadingâ€¦</option>
                  ) : indigenousList.length > 0 ? (
                    indigenousList.map((i) => (<option key={i.id} value={i.id}>{i.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F label={<>Do you speak a language other than English at home? {REQ}</>}>
                <select required className="wc-input" value={d.speakOtherLanguage} onChange={(e) => set("speakOtherLanguage", e.target.value)} data-testid="fn-speaks">
                  <option value="">Chooseâ€¦</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              {d.speakOtherLanguage === "Yes" && (
                <F label={<>Please specify which language(s) {REQ}</>} full><textarea required rows={2} className="wc-input resize-none" value={d.otherLanguage} onChange={(e) => set("otherLanguage", e.target.value)} data-testid="fn-otherlang" /></F>
              )}
              <F label={<>How well do you speak English? {REQ}</>} full>
                <select required className="wc-input max-w-xs" value={d.englishProficiencyId || d.englishProficiency} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (englishList.find(x => String(x.id) === String(val)) || { name: val });
                      set("englishProficiencyId", val);
                      set("englishProficiency", sel.name);
                    }} data-testid="fn-english">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (
                    <option disabled>Loadingâ€¦</option>
                  ) : englishList.length > 0 ? (
                    englishList.map((e) => (<option key={e.id} value={e.id}>{e.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
            </Grid>
          </Section>

          {/* Schooling */}
          <Section id="schooling" title="Schooling" onFocus={() => setActive("schooling")}>
            <Grid>
              <F label={<>Have you completed Year 12 of secondary school? {REQ}</>}>
                <select required className="wc-input" value={d.completedYear12} onChange={(e) => set("completedYear12", e.target.value)} data-testid="fn-completedYear12">
                  <option value="">Chooseâ€¦</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              <F label={<>What is your highest completed year of secondary school? {REQ}</>}>
                <select required className="wc-input" value={d.highestSchoolLevelId || d.highestSchoolLevel} onChange={(e) => {
                      const val = e.target.value;
                      const sel = (highestSchoolList.find(x => String(x.id) === String(val)) || { name: val });
                      set("highestSchoolLevelId", val);
                      set("highestSchoolLevel", sel.name);
                    }} data-testid="fn-highestyr">
                  <option value="">Chooseâ€¦</option>
                  {masterLoading ? (
                    <option disabled>Loadingâ€¦</option>
                  ) : highestSchoolList.length > 0 ? (
                    highestSchoolList.map((h) => (<option key={h.id} value={h.id}>{h.name}</option>))
                  ) : (
                    <option disabled>No data found</option>
                  )}
                </select>
              </F>
              <F label={<>In which year did you complete that level? {REQ}</>}><input required inputMode="numeric" maxLength={4} className="wc-input" value={d.schoolCompletedYear} onChange={(e) => set("schoolCompletedYear", e.target.value)} placeholder="e.g. 2015" data-testid="fn-yrcomp" /></F>
            </Grid>
          </Section>

          {/* Education */}
          <Section id="education" title="Education" onFocus={() => setActive("education")}>
            <div className="mb-3 text-sm text-slate-600">If you have completed Year 12, tick any applicable qualifications:</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {highestQualificationsList.map((qualification) => {
                const qualificationId = String(qualification.id);
                return (
                <label key={qualificationId} className="flex items-center gap-2 p-2.5 rounded-md hover:bg-slate-50 text-sm">
                  <input type="checkbox" checked={d.highestQualificationIds.includes(qualificationId)} onChange={() => set("highestQualificationIds", cbToggle(d.highestQualificationIds, qualificationId))} data-testid={`fn-cert-${qualificationId}`} />
                  <span>{qualification.name}</span>
                </label>
              );})}
            </div>
            <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 leading-relaxed">
              We are part of Nationally Registered Training. Please advise if you have had any prior accredited training in the VET Sector and if you have been granted credit for any unit / subject. This information is vital for our funding and reporting purposes.
            </div>
            <Grid className="mt-4">
              <F label={<>Do you consider yourself able to complete the qualification(s)? {REQ}</>}>
                <select required className="wc-input" value={d.ableToCompleteQualification} onChange={(e) => set("ableToCompleteQualification", e.target.value)} data-testid="fn-ablecomplete">
                  <option value="">Chooseâ€¦</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              {d.ableToCompleteQualification === "Yes" && (
                <F label="In which year did you complete the qualification(s)?"><input type="number" inputMode="numeric" min="1900" max={new Date().getFullYear()} step="1" className="wc-input" value={d.qualificationCompletedYear} onChange={(e) => set("qualificationCompletedYear", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="fn-qualyear" /></F>
              )}
            </Grid>
          </Section>

          {/* Employment */}
          <Section id="employment" title="Employment" onFocus={() => setActive("employment")}>
            <Grid>
              <F label={<>Do you hold a health-care card, pensioner card or VEVO card? {REQ}</>}>
                <select required className="wc-input" value={d.hasConcessionCard} onChange={(e) => set("hasConcessionCard", e.target.value)} data-testid="fn-conccard">
                  <option value="">Chooseâ€¦</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              {d.hasConcessionCard === "Yes" && (
                <F label="Concession card number"><input className="wc-input" value={d.concessionCardNumber} onChange={(e) => set("concessionCardNumber", e.target.value)} data-testid="fn-concno" /></F>
              )}
              <F label="Employment status"><input className="wc-input" value={d.employmentStatus} onChange={(e) => set("employmentStatus", e.target.value)} data-testid="fn-employment-status" /></F>
              <F label="Job title"><input className="wc-input" value={d.occupation} onChange={(e) => set("occupation", e.target.value)} data-testid="fn-jobtitle" /></F>
              <F label="Employment start date"><input type="date" className="wc-input" value={d.employmentStartDate} onChange={(e) => set("employmentStartDate", e.target.value)} data-testid="fn-jobstart" /></F>
              <F label="Average hours worked per week"><input type="number" inputMode="numeric" min="0" step="1" className="wc-input" value={d.workingHoursPerWeek} onChange={(e) => set("workingHoursPerWeek", e.target.value.replace(/\D/g, ""))} data-testid="fn-jobhours" /></F>
              <F label="Employer name and location"><input className="wc-input" value={d.employerName} onChange={(e) => set("employerName", e.target.value)} data-testid="fn-jobemployer" /></F>
            </Grid>
          </Section>

          {/* Disability */}
          <Section id="disability" title="Disability" onFocus={() => setActive("disability")}>
            <F label={<>Do you consider yourself to have a disability, impairment or long-term condition? {REQ}</>}>
              <div className="flex gap-4">
                {["Yes", "No"].map((o) => <label key={o} className="inline-flex items-center gap-2 text-sm"><input type="radio" name="dis" value={o} checked={d.disabilityFlag === o} onChange={(e) => set("disabilityFlag", e.target.value)} data-testid={`fn-dis-${o.toLowerCase()}`} />{o}</label>)}
              </div>
            </F>
            {d.disabilityFlag === "Yes" && (
              <>
                <div className="mt-4 text-sm text-slate-600 mb-2">Please tick your area(s) of disability, impairment or long-term condition:</div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {disabilityTypesList.map((disability) => {
                    const disabilityId = String(disability.id);
                    return (
                    <label key={disabilityId} className="flex items-center gap-2 p-2.5 rounded-md hover:bg-slate-50 text-sm">
                      <input type="checkbox" checked={d.disabilityTypeIds.includes(disabilityId)} onChange={() => set("disabilityTypeIds", cbToggle(d.disabilityTypeIds, disabilityId))} data-testid={`fn-disarea-${disabilityId}`} />
                      <span>{disability.name}</span>
                    </label>
                  );})}
                </div>
                <F label="Additional support required (optional)" full>
                  <textarea rows={3} className="wc-input resize-none" value={d.disabilitySupportRequired} onChange={(e) => set("disabilitySupportRequired", e.target.value)} placeholder="Let us know what adjustments would help you succeedâ€¦" data-testid="fn-dissupport" />
                </F>
              </>
            )}
          </Section>

          {/* Study reason */}
          <Section id="study" title="Study reason" onFocus={() => setActive("study")}>
            <Grid>
              <F label={<>How did you hear about this course? {REQ}</>}>
                <select required className="wc-input" value={d.howDidYouHear} onChange={(e) => set("howDidYouHear", e.target.value)} data-testid="fn-hearabout">
                  <option value="">Chooseâ€¦</option><option>Internet search</option><option>Friend or family</option><option>Colleague</option><option>Employer</option><option>Job Network Provider</option><option>Newspaper / Magazine</option><option>Radio / TV</option><option>Other</option>
                </select>
              </F>
              <F label={<>Able to complete the course with reasonable adjustment to your needs? {REQ}</>}>
                <select required className="wc-input" value={d.reasonableAdjustment} onChange={(e) => set("reasonableAdjustment", e.target.value)} data-testid="fn-adjust">
                  <option value="">Chooseâ€¦</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
              <F label={<>Able to efficiently write and recite in English? {REQ}</>}>
                <select required className="wc-input" value={d.canReadWriteEnglish} onChange={(e) => set("canReadWriteEnglish", e.target.value)} data-testid="fn-writeen">
                  <option value="">Chooseâ€¦</option>
                  {yesNoOptions.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
                </select>
              </F>
            </Grid>
          </Section>

          {/* LLN */}
          <Section id="lln" title="Language, Literacy and Numeracy" onFocus={() => setActive("lln")}>
            <F label="Please describe any learning support you may need for Language, Literacy or Numeracy (optional)" full>
              <textarea rows={3} className="wc-input resize-none" value={d.llnSupportRequired} onChange={(e) => set("llnSupportRequired", e.target.value)} data-testid="fn-lln" />
            </F>
          </Section>

          <Section id="documents" title="My Documents" onFocus={() => setActive("documents")}>
            <StudentEnrollmentDocuments
              documents={documents}
              onDocumentsChange={setDocuments}
            />
          </Section>

          {/* Declaration */}
          <Section id="declaration" title="Declaration" onFocus={() => setActive("declaration")}>
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 leading-relaxed max-h-64 overflow-y-auto">
              <p className="font-semibold text-slate-700 mb-2">Privacy Notice</p>
              <p>In accordance with the Student Identifiers Act 2011, White Card VIC is required to collect personal information about you and disclose it to the National Centre for Vocational Education Research Ltd (NCVER). NCVER is an Australian Government agency whose functions are to collect, manage and disseminate vocational education and training (VET) statistics.</p>
              <p className="mt-2">Personal information disclosed to NCVER may be used or disclosed for the following purposes: improving the quality of training and education; statistical analysis and research related to education; workforce development and labour-market analysis; policy analysis, evaluation, regulation and accreditation.</p>
              <p className="mt-2">You may opt out of any NCVER survey at the time of being contacted. NCVER may provide personal information to third parties where authorised by law, including higher-education authorities and Commonwealth / State / Territory government departments.</p>
              <p className="mt-2 font-semibold text-slate-700">By ticking the boxes below you declare that:</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>The information provided is true and correct to the best of your knowledge.</li>
                <li>You consent to the collection, use and disclosure of your personal information in accordance with this notice.</li>
                <li>Giving false or incomplete information may lead to refusal or disqualification of your enrolment.</li>
                <li>You understand it is your responsibility to provide all documentation as requested throughout the program.</li>
                <li>You have provided a valid USI or have received guidance on how to obtain one.</li>
              </ul>
            </div>
            <label className="mt-4 flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-lg cursor-pointer hover:border-orange-400">
              <input type="checkbox" className="mt-1" checked={d.usiDeclarationAccepted} onChange={(e) => set("usiDeclarationAccepted", e.target.checked)} data-testid="fn-decl-usi" />
              <span className="text-sm text-slate-700">I have provided my valid Unique Student Identifier (USI) {REQ}</span>
            </label>
            <label className="mt-2 flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-lg cursor-pointer hover:border-orange-400">
              <input type="checkbox" className="mt-1" checked={d.declarationAccepted} onChange={(e) => set("declarationAccepted", e.target.checked)} data-testid="fn-decl-terms" />
              <span className="text-sm text-slate-700">I accept the terms and conditions described in the privacy notice above {REQ}</span>
            </label>
            <div className="mt-4 max-w-xl">
              <F label={<>Full name (electronic signature) {REQ}</>}>
                <input required className="wc-input" value={d.studentSignature} onChange={(e) => set("studentSignature", e.target.value)} data-testid="fn-signature" />
              </F>
            </div>
          </Section>

          {/* Actions */}
          <div className="wc-card p-4 sticky bottom-4 z-10 flex flex-col sm:flex-row items-center gap-3 justify-between">
            <div className="text-xs text-slate-500">
              <span className="font-semibold text-slate-700">{requiredCount.done}</span> of {requiredCount.total} required fields completed
              <div className="mt-1 h-1.5 w-64 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${requiredCount.percentage}%` }} />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <button type="button" onClick={cancel} className="text-sm font-semibold text-rose-600 hover:bg-rose-50 border border-rose-200 px-4 py-2.5 rounded-lg inline-flex items-center gap-1.5" data-testid="fn-cancel"><X className="w-4 h-4" /> Cancel</button>
              <button type="button" onClick={saveAndReturn} disabled={saving} className="wc-btn-outline text-sm" data-testid="fn-save"><Save className="w-4 h-4" /> {saving ? "Savingâ€¦" : "Save & return"}</button>
              <button type="submit" disabled={!canSubmit || saving} className="wc-btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="fn-submit"><Send className="w-4 h-4" /> {saving ? "Saving…" : workFlowStatusNo === EnrollmentStatus.Draft ? "Submit" : "Save and Submit"}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ id, title, children, onFocus }) {
  return (
    <section id={`sec-${id}`} onFocus={onFocus} className="wc-card p-6 scroll-mt-24" data-testid={`section-${id}`}>
      <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-200">
        <div className="w-1 h-6 bg-orange-500 rounded-full" />
        <h2 className="text-lg font-display font-extrabold text-[#0A2540]">{title}</h2>
      </div>
      {children}
    </section>
  );
}
function Grid({ children, className = "" }) { return <div className={`grid sm:grid-cols-2 gap-4 ${className}`}>{children}</div>; }
function F({ label, children, hint, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="wc-label flex items-center gap-1.5">{label}{hint && <span className="text-slate-400 font-normal text-xs" title={hint}><Info className="w-3.5 h-3.5 inline" /></span>}</label>
      {children}
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}


// ============ Submission View ============
function SubmissionView({ submission, masterData = {}, documents = [], setDocuments, studentEnrollmentId }) {
  const [showWorkflowHistory, setShowWorkflowHistory] = useState(false);
  const { ref, submittedAt, data: d } = submission;
  const dash = (v) => v && v !== "" ? (Array.isArray(v) ? (v.length ? v.join(", ") : "â€”") : String(v)) : "â€”";
  const optionNames = (selectedIds = [], options = []) => selectedIds.map((selectedId) => {
    const option = options.find((item) => String(item.id) === String(selectedId));
    return option?.name || String(selectedId);
  });
  const optionName = (selectedId, options = [], fallback = "") =>
    options.find((item) => String(item.id) === String(selectedId))?.name || fallback;
  const qualificationNames = optionNames(d.highestQualificationIds, masterData?.highestQualifications || []);
  const disabilityNames = optionNames(d.disabilityTypeIds, masterData?.disabilityTypes || []);
  const samePostalAddress = d.sameAsResidential !== false;
  const postalValue = (postalField, residentialField) => samePostalAddress ? d[residentialField] : d[postalField];
  const groups = [
    { title: "Enrolment", rows: [["Enrolment reference", ref], ["Student enrolment ID", d.studentEnrollmentId], ["Student booking ID", d.studentBookingId]] },
    { title: "Unique Student Identifier", rows: [["USI", d.usiNumber]] },
    { title: "Personal details", rows: [
      ["Title", d.title], ["First name", d.firstName], ["Middle name(s)", d.middleName], ["Surname", d.lastName],
      ["Date of birth", d.dateOfBirth], ["Gender", d.gender], ["Email", d.email],
      ["Mobile", d.mobileNumber], ["Home phone", d.homePhone], ["Work phone", d.workPhone],
      ["Preferred contact", optionName(d.preferredContactTypeId, masterData?.preferredContactTypes)],
    ]},
    { title: "Residential address", rows: [
      ["Building / property name", d.buildingPropertyName], ["Flat / unit details", d.flatUnitDetails],
      ["Street number", d.streetNumber], ["Street name", d.streetName], ["Suburb / locality", d.suburb],
      ["City", optionName(d.cityId, masterData?.cities)], ["State", optionName(d.stateId, masterData?.states, d.state)],
      ["Postcode", d.postCode], ["Country", optionName(d.countryId, masterData?.countries)],
    ]},
    { title: "Postal address", rows: [
      ["Same as residential", samePostalAddress ? "Yes" : "No"],
      ["Building / property name", postalValue("postalBuildingPropertyName", "buildingPropertyName")],
      ["Flat / unit details", postalValue("postalFlatUnitDetails", "flatUnitDetails")],
      ["Street number", postalValue("postalStreetNumber", "streetNumber")], ["Street name", postalValue("postalStreetName", "streetName")],
      ["Suburb / locality", postalValue("postalSuburb", "suburb")],
      ["City", optionName(samePostalAddress ? d.cityId : d.postalCityId, masterData?.postalCities || masterData?.cities)],
      ["State", optionName(samePostalAddress ? d.stateId : d.postalStateId, masterData?.postalStates || masterData?.states, postalValue("postalState", "state"))],
      ["Postcode", postalValue("postalPostCode", "postCode")],
      ["Country", optionName(samePostalAddress ? d.countryId : d.postalCountryId, masterData?.postalCountries || masterData?.countries)],
    ]},
    { title: "Emergency contact", rows: [
      ["Contact name", d.emergencyContactName],
      ["Relationship", optionName(d.emergencyContactRelationshipId, masterData?.emergencyRelationships, d.emergencyRelationship)],
      ["Phone", d.emergencyPhone],
    ]},
    { title: "Language and cultural diversity", rows: [
      ["Country of birth", d.countryOfBirth], ["Town/city of birth", d.townOfBirth],
      ["Australian citizen", d.australianCitizen], ["Indigenous status", d.indigenousStatus],
      ["Speaks other language?", d.speakOtherLanguage], ...(d.speakOtherLanguage === "Yes" ? [["Other language(s)", d.otherLanguage]] : []),
      ["English proficiency", d.englishProficiency],
    ]},
    { title: "Schooling", rows: [["Completed Year 12", d.completedYear12], ["Highest school level", d.highestSchoolLevel], ["School completion year", d.schoolCompletedYear]] },
    { title: "Education", rows: [["Qualifications", qualificationNames], ["Able to complete qualifications", d.ableToCompleteQualification], ["Qualification completion year", d.qualificationCompletedYear]] },
    { title: "Employment", rows: [
      ["Has concession card", d.hasConcessionCard], ["Concession card number", d.concessionCardNumber],
      ["Employment status", d.employmentStatus], ["Job title / occupation", d.occupation], ["Employment start date", d.employmentStartDate],
      ["Average hours per week", d.workingHoursPerWeek], ["Employer name and location", d.employerName],
    ]},
    { title: "Disability", rows: [
      ["Has disability", d.disabilityFlag], ["Disabilities", disabilityNames], ["Support required", d.disabilitySupportRequired],
    ]},
    { title: "Study and support", rows: [
      ["How did you hear about the course", d.howDidYouHear], ["Can complete with reasonable adjustment", d.reasonableAdjustment],
      ["Can efficiently write and recite in English", d.canReadWriteEnglish], ["LLN support required", d.llnSupportRequired],
    ]},
    { title: "Declaration", rows: [
      ["USI declaration accepted", d.usiDeclarationAccepted ? "Yes" : "No"],
      ["Enrolment declaration accepted", d.declarationAccepted ? "Yes" : "No"], ["Student signature", d.studentSignature],
    ]},
  ];

  const documentsEditable = Number(submission.workFlowStatusNo) === EnrollmentStatus.DocumentsPending;
  const [savingDocuments, setSavingDocuments] = useState(false);
  const savePendingDocuments = async () => {
    const pending = documents.filter((document) => document.file);
    if (pending.length === 0) {
      ToastHelper.error("Select at least one replacement document.");
      return;
    }
    setSavingDocuments(true);
    try {
      for (const document of pending) {
        await studentDocumentsService.upload({
          studentDocTypeId: document.studentDocTypeId,
          studentEnrollmentId: Number(studentEnrollmentId),
          issueDate: document.issueDate,
          expiryDate: document.expiryDate,
          remarks: document.remarks,
          documentDescription: document.documentDescription,
          file: document.file,
        });
      }
      const refreshed = await studentDocumentsService.getEnrollmentDocuments(Number(studentEnrollmentId));
      if (typeof setDocuments === "function") setDocuments(refreshed);
      ToastHelper.success("Documents replaced successfully.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to replace documents.");
    } finally {
      setSavingDocuments(false);
    }
  };

  const downloadPdf = () => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 16;
    const contentWidth = pageWidth - (margin * 2);
    let y = 18;

    const clean = (value) => String(value ?? "").replace(/[^\x20-\x7E]/g, "-");
    const ensureSpace = (height = 8) => {
      if (y + height <= pageHeight - margin) return;
      pdf.addPage();
      y = 18;
    };

    pdf.setTextColor(10, 37, 64);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("Student enrolment submission", margin, y);
    y += 9;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(71, 85, 105);
    pdf.text(`Reference: ${clean(ref)}`, margin, y);
    y += 5;
    pdf.text(`Submitted: ${clean(new Date(submittedAt).toLocaleString("en-AU"))}`, margin, y);
    y += 10;

    groups.forEach((group) => {
      ensureSpace(14);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.setTextColor(234, 88, 12);
      pdf.text(clean(group.title), margin, y);
      y += 6;

      group.rows.forEach(([label, value]) => {
        const lines = pdf.splitTextToSize(`${clean(label)}: ${clean(dash(value))}`, contentWidth);
        ensureSpace((lines.length * 5) + 2);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text(lines, margin, y);
        y += (lines.length * 5) + 1;
      });

      y += 4;
    });

    const safeRef = clean(ref || "enrolment").replace(/[^a-zA-Z0-9_-]/g, "-");
    pdf.save(`student-enrolment-${safeRef}.pdf`);
  };

  return (
    <div className="space-y-6" data-testid="submission-view">
      <StatusProgress status={submission.workFlowStatusNo} />
      <div className="wc-card p-6 bg-gradient-to-br from-emerald-50 via-white to-white border-emerald-200 print:border-slate-200">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><CheckCircle2 className="w-7 h-7" /></div>
          <div className="flex-1">
            <div className="text-xs uppercase font-semibold tracking-wide text-emerald-700">Read-only enrolment summary</div>
            <h1 className="text-2xl font-display font-extrabold text-[#0A2540] mt-1">Enrolment {ref}</h1>
            <p className="text-sm text-slate-600 mt-1">Submitted on {new Date(submittedAt).toLocaleString("en-AU", { dateStyle: "long", timeStyle: "short" })}. Our team will contact you within 1 business day at <span className="font-semibold">{d.email || "your email"}</span>.</p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <button onClick={() => window.print()} className="wc-btn-outline text-sm" data-testid="sub-print"><Printer className="w-4 h-4" /> Print</button>
            <button type="button" onClick={downloadPdf} className="wc-btn-outline text-sm" data-testid="sub-download"><Download className="w-4 h-4" /> Download PDF</button>
          </div>
        </div>
      </div>

      <div className="wc-card p-6">
        <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-200">
          <div className="w-1 h-6 bg-orange-500 rounded-full" />
          <h2 className="text-lg font-display font-extrabold text-[#0A2540]">Submitted information</h2>
          <span className="ml-auto text-xs text-slate-500 font-mono">Ref: {ref}</span>
        </div>
        <div className="space-y-6">
          {groups.map((g) => (
            <div key={g.title} data-testid={`sub-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-xs uppercase font-semibold tracking-wide text-orange-600 mb-2">{g.title}</div>
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {g.rows.map(([label, value]) => (
                  <div key={label} className="flex items-start gap-2 py-1.5 border-b border-dashed border-slate-100 last:border-b-0">
                    <dt className="text-slate-500 w-40 shrink-0">{label}</dt>
                    <dd className="text-slate-900 font-medium flex-1 break-words">{dash(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>

      <div className="wc-card p-6 print:hidden">
        <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-200">
          <div className="w-1 h-6 bg-orange-500 rounded-full" />
          <h2 className="text-lg font-display font-extrabold text-[#0A2540]">My Documents</h2>
        </div>
        <StudentEnrollmentDocuments documents={documents} setDocuments={setDocuments} readOnly={!documentsEditable} />
        {documentsEditable && <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4"><p className="text-sm text-orange-800">Admin requested updated documents. Replace the required files, then save them.</p><button type="button" className="wc-btn-primary shrink-0" disabled={savingDocuments} onClick={savePendingDocuments}>{savingDocuments ? "Saving..." : "Save documents"}</button></div>}
      </div>

      <div className="wc-card p-6 print:hidden">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-display font-extrabold text-[#0A2540]">Workflow history</h2>
          <button type="button" className="wc-btn-outline text-sm" onClick={() => setShowWorkflowHistory((value) => !value)}>
            {showWorkflowHistory ? "Hide history" : "View workflow history"}
          </button>
        </div>
        {showWorkflowHistory && <div className="mt-5"><EnrollmentWorkflowTimeline studentEnrollmentId={studentEnrollmentId} loadHistory={studentEnrollmentWorkflowService.getWorkflowHistory} /></div>}
      </div>

      <div className="wc-card p-5 print:hidden">
        <div className="text-sm text-slate-600">{documentsEditable ? "Only the My Documents section is editable while documents are pending." : "Need to make a change? Contact us within 24 hours to amend your submission."}</div>
      </div>
    </div>
  );
}

function StatusProgress({ status }) {
  const numericStatus = Number(status || 1);
  const meta = getStatusMeta(numericStatus);
  const activeIndex = Math.max(0, EnrollmentStatusTimeline.findIndex((stage) => stage.statuses.includes(numericStatus)));
  return (
    <div className="mt-4 space-y-2" data-testid="enrolment-status-progress">
      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.className}`}>{meta.label}</span>
      <div className="flex max-w-2xl gap-1" aria-label={`Current enrolment status: ${meta.label}`}>
        {EnrollmentStatusTimeline.map((stage, index) => (
          <div key={stage.label} className="min-w-0 flex-1">
            <div className={`h-1.5 rounded-full ${index <= activeIndex ? "bg-orange-500" : "bg-slate-200"}`} />
            <div className="mt-1 truncate text-[10px] text-slate-500">{stage.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
